<template>
  <page-content-sidebar-items>
    <div
      v-for="(item, index) in items"
      :key="item?.title"
      class="page-content-sidebar-list__item"
      :class="{ 'page-content-sidebar-list__item_margin-top': !resetItemMarginTop }"
    >
      <page-content-sidebar-list-item
        v-if="item && !item.hidden"
        :ref="(el) => setPageContentSidebarListItemComponents(el, item)"
        v-bind="item"
        :item="item"
        :highlight-exact-active="highlightExactActive"
        :in-group="inGroup"
        :toggle-icon-font-size="toggleIconFontSize"
        @click="$emit('click', $event)"
        @action-click="$emit('action-click', $event)"
        @expand="$emit('expand', item)"
        @collapse="$emit('collapse', item)"
      >
        <template v-slot:title>
          <slot
            name="title"
            :title="item.title"
          />
        </template>
        <template v-slot:after-link-content>
          <slot
            name="after-link-content"
            v-bind="{ item }"
          />
        </template>
        <template v-slot:items="{ items }">
          <slot
            name="items"
            :items="items"
          />
        </template>

        <template v-slot:icon>
          <slot
            name="icon"
            :item="item"
          />
        </template>
        <template v-slot:menu-item-counter>
          <slot
            name="menu-item-counter"
            :item="item"
          />
        </template>
      </page-content-sidebar-list-item>
      <slot
        name="after-list-item"
        :index="index"
      />
    </div>
  </page-content-sidebar-items>
</template>

<script setup lang="ts">
import { ref, type ComponentPublicInstance } from '@/composition/vue/compositionApi'
import {
  isPageContentSidebarGroupCollapsible,
  type PageContentSidebarItem,
  type PageContentSidebarItemAction,
} from '@/components/layout/pageContent/sidebar/types'
import PageContentSidebarListItem from '@/components/layout/pageContent/sidebar/sidebarItem/PageContentSidebarListItem.vue'
import PageContentSidebarItems from '@/components/layout/pageContent/sidebar/PageContentSidebarItems.vue'
import TmLogicError from '@/core/error/tmLogicError'

defineProps<{
  items: PageContentSidebarItem[]
  highlightExactActive?: boolean
  resetItemMarginTop?: boolean
  inGroup?: boolean
  toggleIconFontSize?: number
}>()

defineEmits<{
  'action-click': [PageContentSidebarItemAction]
  click: [PageContentSidebarItem]
  collapse: [PageContentSidebarItem]
  expand: [PageContentSidebarItem]
}>()

const pageContentSidebarListItemComponents = ref<Record<string, InstanceType<typeof PageContentSidebarListItem>>>({})

const setPageContentSidebarListItemComponents = (
  el: Element | ComponentPublicInstance | null,
  item: PageContentSidebarItem,
) => {
  if (!isPageContentSidebarGroupCollapsible(item)) {
    return
  }
  if (!item.itemKey) {
    return
  }
  pageContentSidebarListItemComponents.value[item.itemKey] = el as InstanceType<typeof PageContentSidebarListItem>
}

const changeIsOpenedGroupCollapsible = (itemKey: string, val: boolean) => {
  const component = pageContentSidebarListItemComponents.value[itemKey]
  if (!component) {
    throw new TmLogicError('component is null')
  }
  component.changeIsOpenedGroupCollapsible(val)
}

defineExpose({
  changeIsOpenedGroupCollapsible,
})
</script>

<style lang="scss" scoped>
.page-content-sidebar-list {
  &__item {
    a {
      color: $emphasize;
    }
  }
}
</style>
